// libs
import React, { memo, useCallback, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

// global components
import Portal from 'shared/components/Portal';

// helpers
// TODO - move to common use helpers
import { useNavHelper } from 'shared/components/Header/MobileHeader/helpers';
import { hideOverflowY, resetOverflowY } from 'shared/helpers/dom';

// contexts
import OverlaySheetContext from './OverlaySheetContext';

// constants
import { SHEET_TRANSITION } from './constants';
import { OVERLAY_ZINDEX } from 'shared/styles/base';
import { ROLES } from 'shared/constants';

// styles
import { Container, Overlay } from './styles';
import useFocusTrap from 'shared/hooks/useFocusTrap';

interface IOverlaySheetProps extends RouteComponentProps {
  children: React.ReactNode;
  onHide: VoidFunction;
  navKey: string;
  transitionFrom: SHEET_TRANSITION;
  className?: string;
  allowScroll?: boolean;
  showOverlay?: boolean;
  disableNav?: boolean;
  hideOnOverlayClick?: boolean;
  overlayColor?: string;
  overlayZindex?: number;
  ariaLabel?: string;
  closeOnEscape?: boolean;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

function OverlaySheet({
  children,
  navKey,
  transitionFrom,
  onHide = EMPTY_FUNC,
  history,
  className = '',
  allowScroll = false,
  showOverlay = true,
  disableNav = false,
  hideOnOverlayClick = true,
  overlayColor = '',
  overlayZindex = OVERLAY_ZINDEX,
  ariaLabel,
  closeOnEscape = false,
}: IOverlaySheetProps) {
  const [hide, onHideSheet] = useNavHelper(history, navKey, disableNav);
  const modalRef = useFocusTrap(!hide, { autoFocusDelay: 300 });
  const overlayClickAction = () => {
    if (hideOnOverlayClick) {
      onHideSheet();
    }
  };

  useEffect(() => {
    if (allowScroll === false) {
      hideOverflowY('body', false);
      return resetOverflowY;
    }
  }, [allowScroll]);

  const onAnimationEnd = useCallback((e: React.AnimationEvent) => {
    // Ignore bubbled animation events from children
    if (e.currentTarget !== e.target) {
      return;
    }
    if (hide === false) {
      return;
    }

    onHide();
    resetOverflowY();
  }, [hide, onHide]);

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!closeOnEscape || hide || event.key !== 'Escape' || event.defaultPrevented) return;
    // A nested dialog must handle its own dismissal first.
    const target = event.target as HTMLElement;
    if (target.closest('[role="dialog"]') !== event.currentTarget) return;
    event.preventDefault();
    event.stopPropagation();
    onHideSheet();
  }, [closeOnEscape, hide, onHideSheet]);

  return (
    <Portal>
      <OverlaySheetContext.Provider value={{ doHide: onHideSheet }}>
        {showOverlay && !hide && (
          <Overlay
            overlayColor={overlayColor}
            onClick={overlayClickAction}
            data-test="os-overlay"
            overlayZindex={overlayZindex}
          />
        )}
        <Container
          transition={transitionFrom}
          hide={hide}
          onAnimationEnd={onAnimationEnd}
          data-test="os-content"
          className={className}
          aria-modal="true"
          aria-label={ariaLabel}
          onKeyDown={onKeyDown}
          role={ROLES.DIALOG}
          overlayZindex={overlayZindex}
        >
          <div ref={modalRef} style={{ height: '100%', width: '100%' }}>
            {children}
          </div>
        </Container>
      </OverlaySheetContext.Provider>
    </Portal>
  );
}

export default withRouter(memo(OverlaySheet));
